import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MessageCircle, MapPin, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import PublicLayout from "@/components/layout/PublicLayout";
import SEO from "@/components/SEO";
import { Reveal } from "@/components/motion";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { waLink, trackEvent } from "@/lib/whatsapp";

export default function Contact() {
  const { settings } = useSettings();
  const c = settings?.contact || {};
  const wa = settings?.whatsapp || {};
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Please fill name, email and message."); return; }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent! We'll get back to you soon.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <PublicLayout>
      <SEO title="Contact NewStarAds | YouTube Marketing" description="Get in touch with NewStarAds for YouTube promotion, ads management and marketing advice. Chat on WhatsApp, email us or send a business inquiry." />
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16 grid lg:grid-cols-2 gap-14 items-start">
        <Reveal>
          <p className="text-xs uppercase tracking-widest font-semibold text-primary">Contact</p>
          <h1 className="mt-3 font-display font-black text-4xl md:text-5xl tracking-tighter">Talk to a YouTube marketing expert</h1>
          <p className="mt-5 text-lg text-slate-600">Questions about a campaign? Want honest advice? Reach out — we're happy to help, no pressure.</p>
          <div className="mt-8 space-y-4">
            {c.phone && <a href={`tel:${c.phone.replace(/\s/g,"")}`} className="flex items-center gap-3 group"><span className="w-10 h-10 grid place-items-center rounded-xl bg-primary/10 text-primary"><Phone className="w-5 h-5" /></span><span className="font-medium group-hover:text-primary">{c.phone}</span></a>}
            {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-3 group"><span className="w-10 h-10 grid place-items-center rounded-xl bg-primary/10 text-primary"><Mail className="w-5 h-5" /></span><span className="font-medium group-hover:text-primary">{c.email}</span></a>}
            <a href={waLink(wa.number, wa.default_message)} target="_blank" rel="noreferrer" onClick={() => trackEvent("whatsapp_click", { location: "contact" })} className="flex items-center gap-3 group" data-testid="contact-whatsapp"><span className="w-10 h-10 grid place-items-center rounded-xl bg-green-100 text-green-600"><MessageCircle className="w-5 h-5" /></span><span className="font-medium group-hover:text-green-600">Chat on WhatsApp</span></a>
            {c.address && <div className="flex items-center gap-3"><span className="w-10 h-10 grid place-items-center rounded-xl bg-primary/10 text-primary"><MapPin className="w-5 h-5" /></span><span className="font-medium">{c.address}</span></div>}
          </div>
        </Reveal>
        <Reveal delay={0.1} className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm">
          <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">Name *</Label><Input data-testid="contact-name" value={form.name} onChange={set("name")} required /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">Email *</Label><Input data-testid="contact-email" type="email" value={form.email} onChange={set("email")} required /></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">Phone</Label><Input data-testid="contact-phone" value={form.phone} onChange={set("phone")} /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">Subject</Label><Input data-testid="contact-subject" value={form.subject} onChange={set("subject")} /></div>
            </div>
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">Message *</Label><Textarea data-testid="contact-message" rows={4} value={form.message} onChange={set("message")} required /></div>
            <Button type="submit" disabled={loading} className="w-full h-12 rounded-full" data-testid="contact-submit">{loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Send Message"}</Button>
          </form>
        </Reveal>
      </section>
    </PublicLayout>
  );
}
